/**
 * MyQuestionnaires - Patient fixed questionnaire timeline.
 */
import { useEffect, useMemo, useReducer } from "react";
import api from "@/api";
import { toast } from "sonner";
import PatientLayout from "@/components/layout/PatientLayout";
import QuestionnaireFillForm from "@/components/patient/QuestionnaireFillForm";
import { ClipboardList, Clock, ArrowLeft, CheckCircle, FlaskConical, AlertTriangle, CalendarClock, Lock } from "lucide-react";
import { useLanguage } from "@/i18n/index";

const STATUS_META = {
  due_now: { label: { fr: "À faire", ar: "للإنجاز" }, tone: "bg-blue-50 text-blue-800 border-blue-200", icon: ClipboardList, order: 1, actionable: true },
  overdue_window_expired: { label: { fr: "En retard", ar: "متأخر" }, tone: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertTriangle, order: 0, actionable: true },
  not_yet_due: { label: { fr: "À venir", ar: "قادم" }, tone: "bg-slate-50 text-slate-700 border-slate-200", icon: CalendarClock, order: 2, actionable: false },
  completed: { label: { fr: "Fait", ar: "تم" }, tone: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle, order: 3, actionable: false },
  missing_configuration: { label: { fr: "À configurer", ar: "قيد الإعداد" }, tone: "bg-rose-50 text-rose-800 border-rose-200", icon: AlertTriangle, order: 4, actionable: false },
};

export default function MyQuestionnaires() {
  const { t, language } = useLanguage();
  const [state, dispatch] = useReducer((current, action) => {
    switch (action.type) {
      case "loading":
        return { ...current, loading: true };
      case "loaded":
        return { ...current, loading: false, overview: action.overview || { items: [], counts: {} } };
      case "failed":
        return { ...current, loading: false };
      case "active":
        return { ...current, active: action.questionnaire };
      case "list":
        return { ...current, active: null };
      default:
        return current;
    }
  }, { overview: { items: [], counts: {} }, loading: true, active: null });

  const { overview, loading, active } = state;

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      dispatch({ type: "loading" });
      const overviewRes = await api.get("/studies/my-overview");
      dispatch({ type: "loaded", overview: overviewRes.data || { items: [], counts: {} } });
    } catch (err) {
      toast.error(t("patient.questionnaires.loadError"));
      dispatch({ type: "failed" });
    }
  };

  const rows = useMemo(() => buildQuestionnaireRows(overview, language), [overview, language]);

  if (active) {
    return (
      <PatientLayout>
        <div className="max-w-2xl mx-auto px-4 py-6">
          <button
            onClick={() => dispatch({ type: "list" })}
            data-testid="back-to-list-btn"
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> {t("patient.questionnaires.backList")}
          </button>
          <QuestionnaireFillForm
            enrollmentId={active.enrollmentId}
            visitName={active.visitName}
            questionnaireCode={active.questionnaireCode}
            onCompleted={() => {
              dispatch({ type: "list" });
              toast.success(t("patient.questionnaires.completed"));
              fetchOverview();
            }}
          />
        </div>
      </PatientLayout>
    );
  }

  return (
    <PatientLayout>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5" dir={language === "ar" ? "rtl" : "ltr"}>
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="patient-questionnaires-title">
            <ClipboardList className="w-6 h-6 text-indigo-600" />
            {t("patient.questionnaires.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="patient-questionnaires-subtitle">
            {language === "ar" ? "كل استبيان يظهر كبطاقة واضحة مع حالته." : "Chaque questionnaire est une carte claire avec son statut."}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400" data-testid="loading-state">
            {t("loading")}
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-8 text-center" data-testid="empty-state">
            <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
            <p className="font-semibold text-emerald-800">{language === "ar" ? "لا يوجد استبيان مخصص حاليا" : "Aucun questionnaire assigné"}</p>
            <p className="text-sm text-emerald-700 mt-1" data-testid="patient-questionnaires-empty-desc">{language === "ar" ? "سيخبرك فريق Hana عندما يصبح هناك متابعة متاحة." : "Votre équipe Hana vous préviendra lorsqu’un suivi sera disponible."}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="questionnaire-fixed-list">
            {rows.map((row, idx) => (
              <QuestionnaireStatusCard
                key={row.id}
                row={row}
                index={idx}
                language={language}
                onOpen={() => dispatch({ type: "active", questionnaire: row })}
              />
            ))}
          </div>
        )}
      </div>
    </PatientLayout>
  );
}

function buildQuestionnaireRows(overview, language = "fr") {
  const rows = [];
  for (const item of overview?.items || []) {
    const base = {
      enrollmentId: item.enrollmentId,
      studyName: item.studyName,
      visitName: item.visitName,
      dueDate: item.dueDate,
      windowDays: item.windowDays,
      daysDiff: item.daysDiff,
      daysLate: item.daysLate,
    };
    for (const q of item.questionnaires || []) {
      rows.push({
        ...base,
        id: `${item.enrollmentId}-${item.visitName}-${q.code}-todo`,
        questionnaireCode: q.code,
        questionnaireName: q.name,
        estimatedMinutes: q.estimatedMinutes,
        status: item.status,
      });
    }
    for (const q of item.completedQuestionnaires || []) {
      rows.push({
        ...base,
        id: `${item.enrollmentId}-${item.visitName}-${q.code}-done`,
        questionnaireCode: q.code,
        questionnaireName: q.name,
        estimatedMinutes: q.estimatedMinutes,
        completedAt: q.completedAt,
        status: "completed",
      });
    }
    if (item.status === "missing_configuration" && !(item.questionnaires || []).length) {
      rows.push({
        ...base,
        id: `${item.enrollmentId}-${item.visitName}-missing`,
        questionnaireCode: null,
        questionnaireName: language === "ar" ? "استبيان قيد الإعداد" : "Questionnaire en préparation",
        status: "missing_configuration",
      });
    }
  }
  return rows.sort((a, b) => {
    const aMeta = STATUS_META[a.status] || STATUS_META.missing_configuration;
    const bMeta = STATUS_META[b.status] || STATUS_META.missing_configuration;
    return aMeta.order - bMeta.order || String(a.dueDate || "").localeCompare(String(b.dueDate || ""));
  });
}

function QuestionnaireStatusCard({ row, index, language, onOpen }) {
  const meta = STATUS_META[row.status] || STATUS_META.missing_configuration;
  const Icon = meta.icon;
  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <FlaskConical className="w-4 h-4 text-indigo-500" />
            <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide" data-testid={`questionnaire-study-${index}`}>{row.studyName}</span>
          </div>
          <h3 className="font-semibold text-slate-800" data-testid={`questionnaire-name-${index}`}>{row.questionnaireName}</h3>
          <p className="text-xs text-slate-500 mt-1" data-testid={`questionnaire-visit-${index}`}>{language === "ar" ? "الزيارة" : "Visite"} : {row.visitName}</p>
        </div>
        <span className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${meta.tone}`} data-testid={`questionnaire-status-${index}`}>
          <Icon className="h-3.5 w-3.5" /> {meta.label[language === "ar" ? "ar" : "fr"]}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <span data-testid={`questionnaire-due-date-${index}`}>{language === "ar" ? "موعده" : "Prévu le"} {row.dueDate || "—"}</span>
        {row.completedAt ? <span data-testid={`questionnaire-completed-at-${index}`}>{language === "ar" ? "تم في" : "Fait le"} {new Date(row.completedAt).toLocaleDateString(language === "ar" ? "ar-MA" : "fr-FR")}</span> : null}
        {row.estimatedMinutes ? (
          <span className="inline-flex items-center gap-1" data-testid={`questionnaire-duration-${index}`}><Clock className="h-3.5 w-3.5" /> ~{row.estimatedMinutes} min</span>
        ) : null}
      </div>
      {row.status === "overdue_window_expired" ? (
        <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid={`questionnaire-late-note-${index}`}>
          {language === "ar" ? `متأخر${row.daysLate ? ` منذ ${row.daysLate} يوم` : ""}. يمكنك الإجابة الآن.` : `En retard${row.daysLate ? ` de ${row.daysLate} j` : ""}. Vous pouvez encore répondre pour votre suivi.`}
        </p>
      ) : null}
      {!meta.actionable ? (
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400" data-testid={`questionnaire-locked-${index}`}>
          <Lock className="h-3.5 w-3.5" /> {language === "ar" ? "الإجابة غير مفتوحة" : "Réponse non ouverte"}
        </div>
      ) : null}
      {meta.actionable ? (
        <div className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#2C5E53] px-4 py-3 text-sm font-black text-white" data-testid={`questionnaire-cta-${index}`}>
          {language === "ar" ? "ابدأ الإجابة" : "Répondre maintenant"}
        </div>
      ) : null}
    </>
  );

  if (meta.actionable) {
    return (
      <button
        type="button"
        onClick={onOpen}
        data-testid={`questionnaire-open-${index}`}
        className="w-full rounded-2xl border border-indigo-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md"
      >
        {content}
      </button>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`questionnaire-static-${index}`}>
      {content}
    </div>
  );
}